export type DownloadableFileType = 'PDF' | 'DOCX' | 'CSV';

const FILE_MIME_TYPES: Record<DownloadableFileType, string> = {
  PDF: 'application/pdf',
  DOCX: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  CSV: 'text/csv',
};

const FILE_EXTENSIONS: Record<DownloadableFileType, string> = {
  PDF: '.pdf',
  DOCX: '.docx',
  CSV: '.csv',
};

export interface DownloadFileOptions {
  base64Content: string;
  fileName: string;
  fileType: DownloadableFileType;
}

export interface DownloadFileFromUrlOptions {
  presignedUrl: string;
  fileName: string;
  fileType: 'PDF' | 'DOCX';
  onProgress?: (attempt: number, maxAttempts: number) => void;
}

export const downloadFileFromUrl = async ({ presignedUrl, fileName, fileType, onProgress }: DownloadFileFromUrlOptions): Promise<void> => {
  const maxRetries = 60; // 5 minutes total (60 attempts * 5 seconds)
  const retryDelay = 5000; // 5 seconds
  
  const attemptDownload = async (attemptNumber: number): Promise<Blob> => {
    const resp = await fetch(presignedUrl, {
      method: 'GET',
      redirect: 'follow',
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
      },
    });
    
    if (!resp.ok) {
      console.error(`Failed to fetch presigned URL (attempt ${attemptNumber}):`, {
        status: resp.status,
        statusText: resp.statusText,
        url: presignedUrl.substring(0, 100) + '...',
      });
      throw new Error(`Failed to download file: ${resp.status} ${resp.statusText}`);
    }
    
    const blob = await resp.blob();
    
    // console.log(`Blob details (attempt ${attemptNumber}):`, {
    //   size: blob.size,
    //   type: blob.type,
    // });
    
    return blob;
  };
  
  try {
    // Initial attempt
    if (onProgress) {
      onProgress(1, maxRetries);
    }
    let blob = await attemptDownload(1);
    
    // Retry loop if blob is empty
    let attempt = 1;
    while (blob.size === 0 && attempt < maxRetries) {
      attempt++;
      if (onProgress) {
        onProgress(attempt, maxRetries);
      }
      // console.log(`File is empty, retrying in ${retryDelay / 1000} seconds (attempt ${attempt}/${maxRetries})...`);
      await new Promise((resolve) => setTimeout(resolve, retryDelay));
      blob = await attemptDownload(attempt);
    }
    
    if (blob.size === 0) {
      throw new Error('Time out has been exceeded');
    }

    const extension = fileType === 'PDF' ? '.pdf' : '.docx';
    const finalFileName = fileName.endsWith(extension) ? fileName : `${fileName}${extension}`;

    const href = URL.createObjectURL(blob);

    // create "a" HTML element with href to file & click
    const link = document.createElement('a');
    link.href = href;
    link.setAttribute('download', finalFileName);
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(href);
  } catch (error) {
    console.error('Error downloading file:', error);
    throw new Error('Failed to download file. Please try again.');
  }
};

export const downloadFileFromBase64 = ({ base64Content, fileName, fileType }: DownloadFileOptions): void => {
  try {
    const byteCharacters = atob(base64Content);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);

    const mimeType = FILE_MIME_TYPES[fileType];

    const blob = new Blob([byteArray], { type: mimeType });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    const extension = FILE_EXTENSIONS[fileType];
    const finalFileName = fileName.endsWith(extension) ? fileName : `${fileName}${extension}`;

    link.download = finalFileName;

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading file:', error);
    throw new Error('Failed to download file. Please try again.');
  }
};

export const generateExportFileName = (
  manualName: string,
  exportType: 'manual' | 'tableOfContents' | 'currentContent',
  contentType?: 'policy' | 'preface',
  contentTitle?: string
): string => {
  const cleanManualName = manualName
    .replace(/[<>:"/\\|?*]/g, '')
    .replace(/\s+/g, '_')
    .trim();

  switch (exportType) {
    case 'manual':
      return `${cleanManualName}_Manual`;
    case 'tableOfContents':
      return `${cleanManualName}_TableOfContents`;
    case 'currentContent':
      if (contentType && contentTitle) {
        const cleanContentTitle = contentTitle
          .replace(/[<>:"/\\|?*]/g, '')
          .replace(/\s+/g, '_')
          .trim();
        return `${cleanManualName}_${contentType}_${cleanContentTitle}`;
      }
      return `${cleanManualName}_Content`;
    default:
      return `${cleanManualName}_Export`;
  }
};

export const sanitizeForFilename = (htmlContent: string): string => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlContent;

  const textContent = tempDiv.textContent || tempDiv.innerText || '';

  return textContent
    .replace(/[<>:"/\\|?*]/g, '')
    .replace(/\s+/g, '_')
    .substring(0, 50)
    .trim();
};
